import clsx from "clsx";
import { ReactComponent as Close } from "assets/close.svg";
import { useContext, useEffect, useState } from "react";
import { UploadContext } from "contexts/UploadContext";

enum FILE_TYPES {
  image = "image",
  video = "video",
  zip = "zip",
}

function getFileType(file: File): FILE_TYPES {
  if (file.type.startsWith("video/")) {
    return FILE_TYPES.video;
  }

  if (
    file.type === "application/zip" ||
    file.type === "application/x-zip-compressed"
  ) {
    return FILE_TYPES.zip;
  }

  return FILE_TYPES.image;
}

export interface GalleryPreviewImagesProps {
  className?: string;
  itemClassName?: string;
  imgClassName?: string;
  counterClassName?: string;
  isRemovable?: boolean;
}

export function GalleryPreviewImages(props: GalleryPreviewImagesProps) {
  const [files, setFiles] = useState<Array<{
    src: string;
    type: FILE_TYPES;
  } | null> | null>(null);

  const { files: _files, removeFile: removeImage } = useContext(UploadContext);

  useEffect(() => {
    setFiles((prev) => {
      prev?.map((file) => file?.src && URL.revokeObjectURL(file.src));

      return _files.map((file) => {
        if (file instanceof File) {
          return {
            src: URL.createObjectURL(file),
            type: getFileType(file),
          };
        }

        return null;
      });
    });
  }, [_files]);

  if (!files) {
    return null;
  }

  return (
    <div className={clsx("grid grid-cols-3 gap-4", props.className)}>
      {files.map((file, idx) => (
        <div
          className={clsx(
            "relative grid place-items-end bg-f5f7ff rounded-xl overflow-clip justify-items-center",
            props.itemClassName
          )}
          key={idx}
        >
          {file !== null && (
            <>
              {props.isRemovable && (
                <button
                  className="absolute top-2 right-2 text-191c20"
                  onClick={() => removeImage(idx)}
                >
                  <Close />
                </button>
              )}
              {file.type === FILE_TYPES.image && (
                <img
                  className={clsx(
                    "aspect-[.79] object-cover",
                    props.imgClassName
                  )}
                  src={file.src}
                  alt=""
                />
              )}
              {file.type === FILE_TYPES.video && (
                <video
                  className={clsx(
                    "aspect-[.79] object-cover",
                    props.imgClassName
                  )}
                  src={file.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              )}
              {file.type === FILE_TYPES.zip && (
                <div
                  className={clsx(
                    "aspect-[.79] w-full flex flex-col items-center justify-center bg-f5f7ff",
                    props.imgClassName
                  )}
                >
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6H12L10 4H4C2.89 4 2 4.89 2 6V18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V8C22 6.89 21.11 6 20 6ZM19 14H16V17H14V14H11V12H14V9H16V12H19V14Z"
                      fill="#3461FD"
                    />
                  </svg>
                  <span className="mt-2 text-sm font-medium text-center">
                    ZIP File
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
